import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TaskListHeader, TaskListOperation } from 'src/app/models/domain';
import { OperationDataService } from 'src/app/services';
import { CharacteristicService } from 'src/app/state/characteristic';
import { CollectionState } from 'src/app/state/collection-state';
import { ItemUiState } from 'src/app/state/item-ui-state';
import { upsertById } from 'src/app/state/upsert-by-id';

export interface IOperationsUI {
  loading: boolean;
}

@Injectable({ providedIn: 'root' })
export class OperationsService {
  private readonly state = new CollectionState<TaskListOperation>();
  private readonly uiState = new ItemUiState<IOperationsUI>();

  public readonly uiById$ = this.uiState.byId$;

  public constructor(
    private readonly operationsDataService: OperationDataService,
    private readonly characteristicService: CharacteristicService
  ) {}

  /**
   * Operations accumulate across every expanded specification row — the id encodes
   * which task-list header they belong to, which is what this filters on.
   */
  public operationsFor({ id }: TaskListHeader): Observable<TaskListOperation[]> {
    return this.state.select(({ items }) =>
      items.filter((operation) => operation.id === `${id}-${operation.nodes}`)
    );
  }

  public loadByTaskListHeader(taskListHeader: TaskListHeader) {
    this.state.setLoading(true);

    return this.operationsDataService
      .getTaskListOperationByTaskLisGroupAndGroupCounter(
        taskListHeader.taskListGroup,
        taskListHeader.groupCounter
      )
      .pipe(
        tap((operations) => {
          // upsert, not append: collapsing and re-expanding a specification row refetches
          // the same operations, and `operationsFor` would then yield each of them twice.
          // Akita's entity store was keyed by id, so it deduplicated implicitly.
          this.state.set(upsertById(this.state.items, operations));
          this.uiState.setMany(operations.map(({ id }) => [id, { loading: true }]));
        })
      );
  }

  public populateCharacteristicsByOperation(operation: TaskListOperation) {
    this.uiState.set(operation.id, { loading: true });

    return this.characteristicService.populateCharacteristicsByOperation(operation).pipe(
      tap(() => {
        this.uiState.set(operation.id, { loading: false });
      })
    );
  }
}
